import React, { useEffect, useState } from "react";
import ReportHeading from "../Components/ReportHeadings/ReportHeadings";
import { motion } from "framer-motion";
import Seo from "../../components/SeoComponent/Seo";
import { ToastContainer } from "react-toastify";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Loader from "../../components/Loader/Loader";
import {
  AddVendor,
  fetchVendor,
  ViewVendor,
  updateVendor,
  VendorStatusCodeReport,
  VendorStatusCodeCreate,
  VendorStatusCodeUpdate,
  VendorStatusCodeView,
  statusCodeAndServiceList,
  VendorStatusCodeDelete,
} from "../../services/VendorService/VendorService";
import {
  Edit,
  Eraser,
  PlusCircle,
  PlusSquare,
  Save,
  Trash,
  Trash2,
  XCircle,
} from "lucide-react";
import ServiceList from "./VendorServices";

const initialForm = {
  name: "",
  status_id: "",
  base_prod_url: "",
  base_uat_url: "",
};

const initialVendorStatusForm = {
  status_code: "",
  status_message: "",
  vendor_id: "",
};

const isValidUrl = (s) => {
  if (!s) return true;
  try {
    new URL(s);
    return true;
  } catch {
    return false;
  }
};

const VendorMaster = () => {
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Vendor Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [addForm, setAddForm] = useState(initialForm);
  const [editForm, setEditForm] = useState(initialForm);
  const [addErrors, setAddErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  // Vendor Status Code Modal
  const [showStatusCodeModal, setShowStatusCodeModal] = useState(false);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [statusForm, setStatusForm] = useState(initialVendorStatusForm);
  const [statusEditId, setStatusEditId] = useState(null);
  const [vendorIdForStatus, setVendorIdForStatus] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [statusCodeTab, setStatusCodeTab] = useState("single");
  const [servicePage, setServicePage] = useState(1);
  const [isEditingStatusCode, setIsEditingStatusCode] = useState(false);
  const [statusIdForEditing, setStatusIdForEditing] = useState("")

  // const [codeArray, setCodeArray] = useState([]);

  // Load vendors
  const loadVendors = async () => {
    setIsLoading(true);
    try {
      const res = await fetchVendor();
      const payload = res?.data ?? res;
      const list =
        payload?.data ||
        payload?.vendors ||
        (Array.isArray(payload) ? payload : []) ||
        [];
      setVendors(list);

      const lastPage = payload?.last_page || payload?.meta?.last_page || 1;
      setTotalPages(lastPage);
    } catch (err) {
      showErrorToast("Failed to fetch vendors");
    } finally {
      setIsLoading(false);
    }
  };

  // Load vendor status codes
  const loadStatusCodes = async (vendorId) => {
    try {
      const res = await statusCodeAndServiceList({ vendor_id: vendorId });
      setVendorDetails(res?.data);
      if (res?.data?.status_codes?.length > 0) {
        setStatusEditId(true);
      }
    } catch (err) {
      showErrorToast("Failed to load status codes");
    }
  };

  const handleEditStatusCode = (data) => {
    setStatusForm({
      status_code: data.status_code,
      status_message: data.status_message,
    });
    setIsEditingStatusCode(true);
    setStatusIdForEditing(data?.id)
  };

  useEffect(() => {
    loadStatusCodes(vendorIdForStatus);
  }, [servicePage]);

  useEffect(() => {
    loadVendors(page);
  }, [page]);

  // Validation
  const validateVendor = (data) => {
    const e = {};
    if (!data.name?.trim()) e.name = "Vendor name is required";
    if (!data.status_id) e.status_id = "Status is required";
    if (data.base_prod_url && !isValidUrl(data.base_prod_url))
      e.base_prod_url = "Invalid Production URL";
    if (data.base_uat_url && !isValidUrl(data.base_uat_url))
      e.base_uat_url = "Invalid UAT URL";
    return e;
  };

  // Vendor handlers
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm((s) => ({ ...s, [name]: value }));
    if (addErrors[name]) setAddErrors({ ...addErrors, [name]: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((s) => ({ ...s, [name]: value }));
    if (editErrors[name]) setEditErrors({ ...editErrors, [name]: "" });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const errors = validateVendor(addForm);
    setAddErrors(errors);
    if (Object.keys(errors).length) return;

    setIsLoading(true);
    try {
      await AddVendor(addForm);
      showSuccessToast("Vendor added successfully");
      setShowAddModal(false);
      setAddForm(initialForm);
      setPage(1);
      loadVendors(1);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to add vendor");
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = async (id) => {
    setIsLoading(true);
    try {
      const res = await ViewVendor({ id });
      const vendorData = res?.data?.data || res?.data || {};
      setEditId(id);
      setEditForm({
        name: vendorData.name ?? "",
        status_id: vendorData.status_id ?? "",
        base_prod_url: vendorData.base_prod_url ?? "",
        base_uat_url: vendorData.base_uat_url ?? "",
      });
      setEditErrors({});
      setShowEditModal(true);
    } catch (err) {
      showErrorToast("Failed to load vendor details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const errors = validateVendor(editForm);
    setEditErrors(errors);
    if (Object.keys(errors).length) return;

    setIsLoading(true);
    try {
      await updateVendor({ id: editId, payload: editForm });
      showSuccessToast("Vendor updated successfully");
      setShowEditModal(false);
      setEditId(null);
      loadVendors(page);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to update vendor");
    } finally {
      setIsLoading(false);
    }
  };

  // Status Code handlers
  const openStatusCodeModal = (vendorId = "", vName) => {
    setVendorIdForStatus(vendorId);
    setVendorName(vName);
    setStatusForm(initialVendorStatusForm);
    setStatusEditId(null);
    if (vendorId) loadStatusCodes(vendorId);
    setShowStatusCodeModal(true);
  };

  const handleStatusChange = (e) => {
    const { name, value } = e.target;
    // if (name === "status_code") {
    //   setCodeArray(prev => [...prev, value])
    // }
    setStatusForm((s) => ({ ...s, [name]: value }));
  };

  const handleStatusSubmit = async () => {
    if (!statusForm.status_message) {
      return showErrorToast("please enter status code");
    } else if (!statusEditId && !statusForm.status_message) {
      return showErrorToast("please enter status message");
    }

    try {
      if (statusEditId) {
        const res = await VendorStatusCodeUpdate({
          id: statusIdForEditing,
          vendor_id: vendorIdForStatus,
          status_code: statusForm.status_code,
          status_message: statusForm.status_message,
        });

        if (res?.status?.code === 200 || res?.status === true) {
          showSuccessToast(
            res?.message ||
              res?.data?.message ||
              res?.data?.data?.message ||
              "Status code updated",
          );

          // setCodeArray([]);
          loadStatusCodes(vendorIdForStatus);
          setIsEditingStatusCode(false);
          setStatusForm({
            status_code:"",
            status_message: ""
          })
        } else {
          console.log(res);

          let err = "";
          Object.entries(res?.data?.errors).forEach(([key, val]) => {
            err = val[0];
          });

          showErrorToast(
            err ||
              res?.message ||
              res?.data?.message ||
              res?.data?.data?.message ||
              "Failed To Update",
          );
        }
      } else {
        const res = await VendorStatusCodeCreate({
          vendor_id: vendorIdForStatus,
          status_code: statusForm.status_code,
          status_message: statusForm.status_message,
        });
        if (res?.status?.code === 200 || res?.status === true) {
          showSuccessToast(
            res?.message ||
              res?.data?.message ||
              res?.data?.data?.message ||
              "Failed To Create",
          );
          // setCodeArray([]);
          loadStatusCodes(vendorIdForStatus);
        } else {
          let err = "";
          Object.entries(res?.data?.errors).forEach(([key, val]) => {
            err = val[0];
          });

          showErrorToast(
            err ||
              res?.message ||
              res?.data?.message ||
              res?.data?.data?.message ||
              "Status code Created",
          );
        }
      }
      loadStatusCodes(vendorIdForStatus);
    } catch (error) {
      showErrorToast("Status code operation failed");
    }
  };

  const openStatusEdit = async (id) => {
    try {
      const res = await VendorStatusCodeView({ id });
      setStatusEditId(id);
      setStatusForm({ status_code: res?.data?.status_code || "" });
    } catch (err) {
      showErrorToast("Failed to load status code");
    }
  };

  // const handleRemoveCode = (code) => {
  //   const filter = codeArray?.filter((item, idx) => code !== item);
  //   setCodeArray(filter);
  // };

  const handleDeleteStatusCode = async (code) => {
    try {
      const res = await VendorStatusCodeDelete({
        vendor_id: vendorIdForStatus,
        status_code: code,
      });

      if (res?.status?.code === 200) {
        showSuccessToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
        await loadStatusCodes(vendorIdForStatus);
      } else {
        showErrorToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    console.log("status Form", statusForm);
  }, [statusForm]);
  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="p-3 md:p-5 max-w-7xl mx-auto"
    >
      <Seo />
      {isLoading && <Loader />}
      <ToastContainer />

      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-1 md:p-3">
        <ReportHeading name="Vendor Master" />

        {/* Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
            >
              + Add Vendor
            </button>
          </div>

          {/* <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
                className="px-2 py-2 border-2 border-gray-300 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isLoading}
                className="px-4 py-2 border-2 border-gray-300 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                Next
              </button>
            </div>
          </div> */}
        </div>

        {/* Vendors Table */}
        <div className="overflow-x-auto mt-2 rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
              <tr>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">
                  #
                </th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">
                  Name
                </th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">
                  Prod URL
                </th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">
                  UAT URL
                </th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">
                  Status
                </th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">
                  Created
                </th>
                <th className="border-b px-2 py-1 text-center font-semibold text-gray-800">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {vendors.length ? (
                vendors.map((v, idx) => (
                  <tr
                    key={v.id ?? idx}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-2 py-1 font-medium text-gray-900">
                      {(page - 1) * 10 + idx + 1}
                    </td>
                    <td className="px-2 py-1 font-semibold text-gray-900 max-w-md truncate">
                      {v.name ?? "-"}
                    </td>
                    <td className="px-2 py-1 text-gray-700 max-w-xs truncate">
                      {v.base_prod_url ? (
                        <a
                          href={v.base_prod_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm"
                        >
                          {v.base_prod_url}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-2 py-1 text-gray-700 max-w-xs truncate">
                      {v.base_uat_url ? (
                        <a
                          href={v.base_uat_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:underline text-sm"
                        >
                          {v.base_uat_url}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          v.status?.value === "active"
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                        }`}
                      >
                        {v.status?.value || "Unknown"}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-sm text-gray-600">
                      {v.created_at
                        ? new Date(v.created_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => openEdit(v.id)}
                          className="px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium shadow-sm hover:shadow-md transition-all"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openStatusCodeModal(v.id, v.name)}
                          className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-medium shadow-sm hover:shadow-md transition-all"
                        >
                          Status Codes
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-2 py-6 text-center text-gray-500"
                  >
                    No vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg w-full max-w-xl p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Add Vendor</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAddSubmit} className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    value={addForm.name}
                    onChange={handleAddChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${addErrors.name ? "border-red-500" : "border-gray-300"}`}
                  />
                  {addErrors.name && (
                    <div className="text-xs text-red-500 mt-1">
                      {addErrors.name}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">
                    Status ID <span className="text-red-500">*</span>
                  </label>

                  <select
                    name="status_id"
                    value={addForm.status_id}
                    onChange={handleAddChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${
                      addErrors.status_id ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="" selected>
                      Select Status
                    </option>
                    <option value="5">Active</option>
                    <option value="4">Inactive</option>
                  </select>

                  {addErrors.status_id && (
                    <div className="text-xs text-red-500 mt-1">
                      {addErrors.status_id}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">Base Prod URL</label>
                  <input
                    name="base_prod_url"
                    value={addForm.base_prod_url}
                    onChange={handleAddChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${addErrors.base_prod_url ? "border-red-500" : "border-gray-300"}`}
                  />
                  {addErrors.base_prod_url && (
                    <div className="text-xs text-red-500 mt-1">
                      {addErrors.base_prod_url}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">Base UAT URL</label>
                  <input
                    name="base_uat_url"
                    value={addForm.base_uat_url}
                    onChange={handleAddChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${addErrors.base_uat_url ? "border-red-500" : "border-gray-300"}`}
                  />
                  {addErrors.base_uat_url && (
                    <div className="text-xs text-red-500 mt-1">
                      {addErrors.base_uat_url}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-700 text-white rounded"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg w-full max-w-xl p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Edit Vendor</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleEditSubmit} className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    value={editForm.name}
                    onChange={handleEditChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${editErrors.name ? "border-red-500" : "border-gray-300"}`}
                  />
                  {editErrors.name && (
                    <div className="text-xs text-red-500 mt-1">
                      {editErrors.name}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">
                    Status ID <span className="text-red-500">*</span>
                  </label>

                  <select
                    name="status_id"
                    value={editForm.status_id}
                    onChange={handleEditChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${
                      editErrors.status_id
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select Status</option>
                    <option value="5">Active</option>
                    <option value="4">Inactive</option>
                  </select>

                  {editErrors.status_id && (
                    <div className="text-xs text-red-500 mt-1">
                      {editErrors.status_id}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">Base Prod URL</label>
                  <input
                    name="base_prod_url"
                    value={editForm.base_prod_url}
                    onChange={handleEditChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${editErrors.base_prod_url ? "border-red-500" : "border-gray-300"}`}
                  />
                  {editErrors.base_prod_url && (
                    <div className="text-xs text-red-500 mt-1">
                      {editErrors.base_prod_url}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm mb-1">Base UAT URL</label>
                  <input
                    name="base_uat_url"
                    value={editForm.base_uat_url}
                    onChange={handleEditChange}
                    className={`w-full border rounded px-3 py-2 text-sm ${editErrors.base_uat_url ? "border-red-500" : "border-gray-300"}`}
                  />
                  {editErrors.base_uat_url && (
                    <div className="text-xs text-red-500 mt-1">
                      {editErrors.base_uat_url}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-700 text-white rounded"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* NEW: Vendor Status Code Modal */}
        {showStatusCodeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 transition-opacity">
            <div className="bg-white rounded-2xl w-full p-4 max-w-4xl max-h-[85vh]  shadow-2xl ring-1 ring-slate-200 overflow-hidden">
              {/* Scrollable Body */}
              <div className="p-2 overflow-y-auto no-scrollbar grid grid-cols-2">
                <div>
                  {/* Sticky Header */}
                  <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md px-6 pt-4 mb-4 flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold mb-1 text-slate-900">
                        Manage Status Codes
                      </h3>
                      <div className="flex gap-3">
                        {" "}
                        <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                          Vendor Name:
                          <span className="font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md text-xs font-medium border border-slate-200">
                            {vendorName}
                          </span>
                        </p>
                        <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                          Vendor ID:
                          <span className="font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md text-xs font-medium border border-slate-200">
                            {vendorIdForStatus}
                          </span>
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {setShowStatusCodeModal(false)
                        setIsEditingStatusCode(false)
                      }   }
                      className="p-2 text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-300 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-200"
                      aria-label="Close modal"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* {codeArray.length > 0 && (
                    <div className="flex flex-wrap gap-2  p-1">
                      {codeArray.map((item, idx) => (
                        <div
                          key={idx}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-slate-200 shadow-sm rounded-lg text-sm font-medium text-slate-700 animate-in fade-in zoom-in-95 duration-200"
                        >
                          <span className="font-mono text-blue-600">
                            {item}
                          </span>

                          <button
                            type="button"
                            onClick={() => handleRemoveCode(item)}
                            className="text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors p-0.5 focus:outline-none focus:ring-2 focus:ring-red-500/30"
                            aria-label={`Remove ${item}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )} */}
                  {/* Status Code Form */}
                  <div className="bg-slate-50 p-5 rounded-xl border border-slate-200/60 mb-4 shadow-sm">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-sm text-slate-700 mb-4 flex items-center gap-1">
                        {isEditingStatusCode
                          ? "Update Status Details"
                          : "Add New Status"}
                        <span className="text-red-500">*</span>
                      </h4>

                    {isEditingStatusCode && 
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setStatusForm({
                              status_code: "",
                              status_message: "",
                            })
                            setIsEditingStatusCode(false)
                          }}
                          className="bg-blue-600 flex items-center gap-2 hover:bg-blue-700 text-white text-xs font-medium py-2 px-3 rounded-lg shadow-sm shadow-blue-600/20 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        >
                         <Eraser size={18}/> Clear
                        </button>
                      </div>
                    }

                      {/* <div className="inline-flex bg-slate-100 p-1 rounded-xl w-full sm:w-fit mb-4">
                        <button
                          type="button"
                          onClick={() => {
                            setStatusForm({
                              status_code: "",
                              status_message: "",
                            });
                            setCodeArray([]);
                            setStatusCodeTab("single");
                          }}
                          className={`flex-1 sm:flex-none px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                            statusCodeTab === "single"
                              ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/60"
                          }`}
                        >
                          Single
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setStatusForm({
                              status_code: "",
                              status_message: "",
                            });
                            setCodeArray([]);
                            setStatusCodeTab("multiple");
                          }}
                          className={`flex-1 sm:flex-none px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                            statusCodeTab === "multiple"
                              ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/60"
                          }`}
                        >
                          Multiple
                        </button>
                      </div> */}
                    </div>

                    <div className="space-y-4">
                      <div className="w-full">
                        <label className="flex items-center justify-between text-xs font-medium text-slate-700 mb-1.5">
                          <span>
                            Status Code <span className="text-red-500">*</span>
                          </span>
                          {/* Subtle hint utilizing the justify-between */}
                          {/* <span className="text-red-700 font-normal italic">
                            {statusCodeTab === "single"
                              ? " Add codes one by one"
                              : " Add multiple codes separated by comma (,)"}
                          </span> */}
                        </label>

                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            name="status_code"
                            placeholder={
                              statusCodeTab === "single"
                                ? "e.g., 404"
                                : "e.g., 404, 500, 401"
                            }
                            value={statusForm.status_code}
                            onChange={handleStatusChange}
                            className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                          />

                          {/* <button
                            type="button"
                            disabled={!statusForm?.status_code}
                            onClick={() => {
                              if (statusForm?.status_code) {
                                if (statusCodeTab === "single") {
                                  setCodeArray((prev) => [
                                    ...prev,
                                    statusForm.status_code,
                                  ]);
                                  setStatusForm((prev) => ({
                                    ...prev,
                                    status_code: "",
                                  }));
                                } else {
                                  setCodeArray(
                                    statusForm?.status_code?.split(","),
                                  );
                                }
                              }
                            }}
                            className="flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <PlusCircle className="w-4 h-4" />
                            <span>Add</span>
                          </button> */}
                        </div>
                      </div>

                      {/* Message Input - Stacked Below & Full Width */}
                      <div className="w-full">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Message
                        </label>
                        <input
                          type="text"
                          name="status_message"
                          placeholder="e.g., Not Found"
                          value={statusForm.status_message || ""}
                          onChange={handleStatusChange}
                          className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                        />
                      </div>

                      {/* Save/Update Button */}
                      <div className="flex justify-end pt-2">
                        <button
                          onClick={handleStatusSubmit}
                          className="bg-blue-600 flex items-center gap-2 hover:bg-blue-700 text-white text-sm font-medium py-2 px-5 rounded-lg shadow-sm shadow-blue-600/20 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        ><Save size={18}/>
                          {isEditingStatusCode
                            ? "Update Status"
                            : "Save Status"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Status Codes List */}
                  <div>
                    {/* Header Section */}
                    <div className="flex items-center justify-between mb-1 px-1">
                      <h4 className="font-semibold text-slate-900 text-sm">
                        Current Status Codes
                      </h4>
                      {/* <span className="bg-slate-100 text-slate-600 text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200">
          {vendorDetails?.status_codes?.length || 0} total
        </span> */}
                    </div>

                    {/* Main Content Area */}
                    {vendorDetails?.status_codes?.length > 0 ? (
                      <div className="space-y-3 p-1 max-h-[200px] overflow-y-auto">
                        {vendorDetails.status_codes.map((statusObj) => (
                          <div
                            key={statusObj.id}
                            className="flex items-start justify-between bg-slate-50 border border-slate-200/60 p-3.5 rounded-xl shadow-sm hover:border-slate-300 transition-colors"
                          >
                            {/* Code & Message */}
                            <div className="flex items-center gap-1.5">
                              <span className="inline-flex items-center bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 rounded-lg text-sm font-medium font-mono w-max">
                                {statusObj.status_code}
                              </span>
                              <p className="text-sm text-slate-600 font-medium">
                                {statusObj.status_message ||
                                  "No message defined"}
                              </p>
                            </div>

                            {/* Actions: Edit & Delete */}
                            <div className="flex items-center gap-1 shrink-0 ml-4">
                              <button
                                type="button"
                                onClick={() => handleEditStatusCode(statusObj)}
                                className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                aria-label={`Edit status code ${statusObj.status_code}`}
                              >
                                <Edit size={16} />{" "}
                                {/* Make sure to import Edit from your icon library */}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteStatusCode( statusObj.status_code)
                                }
                                className="text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/20"
                                aria-label={`Delete status code ${statusObj.status_code}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Empty State */
                      <div className="text-center py-10 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                        <p className="text-sm text-slate-500">
                          No status codes found.
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Add one above to get started.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col justify-between gap-3">
                  <ServiceList
                    page={servicePage}
                    setPage={setServicePage}
                    data={vendorDetails}
                  />
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => {setShowStatusCodeModal(false)
                        setIsEditingStatusCode(false)
                      }}
                      className="bg-blue-600 flex items-center gap-2 hover:bg-blue-700 text-white text-sm font-medium py-2 px-5 rounded-lg shadow-sm shadow-blue-600/20 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    >
                      <XCircle /> Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default VendorMaster;
